import React, { useCallback, useMemo, useState } from "react";
import { Button, Input, Modal, ModalBody, ModalFooter, ModalHeader, Spinner, Table } from "reactstrap";

import { useToast } from "../../hooks/toast";
import { AUTOPILOT_COLORS } from "../autopilot/styles";
import { MAX_CHAVES, nomeChaveValido, pareceChavePublica } from "./sshKey";
import { acessoSftpAdminApi } from "./adminApi";

// "Gerenciar chaves" (PO1-2165/2166): só acesso novo em SSH. Lista (nome, tipo,
// fingerprint, data), inclui até 3 e remove; a última não sai.
// `api` troca as rotas (admin × MeuFrete), ver components/AcessoSftp/index.js.
// `permitirRemoverUltima`: no MeuFrete a transportadora pode ficar sem chave se
// trocou para senha; no admin a última não sai.

function formatarData(v) {
  if (!v) return "—";
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleDateString("pt-BR");
}

export default function ChavesModal({ idEmbarcador, idTransportadora, chaves, onClose, onChanged, api }) {
  const cliente = api || acessoSftpAdminApi;
  const { addToast } = useToast();
  const [lista, setLista] = useState(chaves || []);
  const [nome, setNome] = useState("");
  const [chavePublica, setChavePublica] = useState("");
  const [loading, setLoading] = useState(false);
  const [removendo, setRemovendo] = useState(null);

  const checagem = useMemo(() => pareceChavePublica(chavePublica), [chavePublica]);
  const podeIncluir = lista.length < MAX_CHAVES && nomeChaveValido(nome) && checagem.ok && !loading;

  const incluir = useCallback(async () => {
    try {
      setLoading(true);
      const r = await cliente.incluirChave(idEmbarcador, idTransportadora, { nome: nome.trim(), chavePublica: chavePublica.trim() });
      setLista((l) => l.concat([{ id: r.id, nome: r.nome, tipo: r.tipo, fingerprint: r.fingerprint, criadaEm: new Date().toISOString() }]));
      setNome("");
      setChavePublica("");
      addToast({ place: "tr", type: "success", title: "Chave incluída", description: `${r.nome} · ${r.fingerprint}` });
      if (onChanged) onChanged();
    } catch (e) {
      addToast({ place: "tr", type: "danger", title: "Erro", description: cliente.mensagemErro(e, "Falha ao incluir a chave.") });
    } finally {
      setLoading(false);
    }
  }, [cliente, idEmbarcador, idTransportadora, nome, chavePublica, addToast, onChanged]);

  const remover = useCallback(
    async (chave) => {
      try {
        setRemovendo(chave.id);
        await cliente.removerChave(idEmbarcador, idTransportadora, chave.id);
        setLista((l) => l.filter((c) => c.id !== chave.id));
        addToast({ place: "tr", type: "success", title: "Chave removida", description: chave.nome });
        if (onChanged) onChanged();
      } catch (e) {
        addToast({ place: "tr", type: "danger", title: "Erro", description: cliente.mensagemErro(e, "Falha ao remover a chave.") });
      } finally {
        setRemovendo(null);
      }
    },
    [cliente, idEmbarcador, idTransportadora, addToast, onChanged]
  );

  return (
    <Modal isOpen toggle={onClose} size="lg">
      <ModalHeader toggle={onClose}>Chaves SSH do acesso</ModalHeader>
      <ModalBody>
        {lista.length ? (
          <Table responsive className="hub-thead-accent" style={{ marginBottom: "1rem" }}>
            <thead>
              <tr>
                <th>Nome</th>
                <th>Tipo</th>
                <th>Fingerprint</th>
                <th>Criada em</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {lista.map((c) => (
                <tr key={c.id}>
                  <td style={{ color: AUTOPILOT_COLORS.title }}>{c.nome}</td>
                  <td style={{ color: AUTOPILOT_COLORS.text }}>{c.tipo}</td>
                  <td>
                    <code style={{ color: AUTOPILOT_COLORS.textSoft, fontSize: "0.74rem", wordBreak: "break-all" }}>{c.fingerprint}</code>
                  </td>
                  <td style={{ color: AUTOPILOT_COLORS.text }}>{formatarData(c.criadaEm)}</td>
                  <td style={{ textAlign: "right" }}>
                    <Button
                      color="danger"
                      outline
                      size="sm"
                      disabled={lista.length <= 1 || removendo === c.id}
                      title={lista.length <= 1 ? "A última chave não pode ser removida" : "Remover"}
                      onClick={() => remover(c)}
                    >
                      {removendo === c.id ? <Spinner size="sm" /> : "Remover"}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          <div style={{ color: AUTOPILOT_COLORS.muted, fontSize: "0.82rem", marginBottom: "0.75rem" }}>Nenhuma chave cadastrada ainda.</div>
        )}

        {lista.length < MAX_CHAVES ? (
          <div>
            <div style={{ color: AUTOPILOT_COLORS.muted, fontSize: "0.78rem", marginBottom: "0.3rem" }}>
              Incluir chave ({lista.length}/{MAX_CHAVES}) · ssh-rsa ≥ 2048 ou ssh-ed25519 · só a pública
            </div>
            <Input placeholder="Nome da chave" value={nome} onChange={(e) => setNome(e.target.value)} maxLength={40} style={{ marginBottom: "0.4rem" }} />
            <Input
              type="textarea"
              rows={3}
              placeholder="ssh-rsa AAAA... ou bloco SSH2"
              value={chavePublica}
              onChange={(e) => setChavePublica(e.target.value)}
              style={{ fontFamily: "monospace", fontSize: "0.76rem" }}
            />
            <div style={{ fontSize: "0.76rem", marginTop: "0.3rem", color: checagem.ok ? AUTOPILOT_COLORS.statusSuccess : AUTOPILOT_COLORS.muted }}>
              {chavePublica ? (checagem.ok ? `Formato ${checagem.formato}` : checagem.motivo) : ""}
            </div>
          </div>
        ) : (
          <div style={{ color: AUTOPILOT_COLORS.muted, fontSize: "0.8rem" }}>Limite de {MAX_CHAVES} chaves atingido. Remova uma para incluir outra.</div>
        )}
      </ModalBody>
      <ModalFooter>
        {lista.length < MAX_CHAVES ? (
          <Button color="primary" onClick={incluir} disabled={!podeIncluir}>
            {loading ? <Spinner size="sm" /> : "Incluir chave"}
          </Button>
        ) : null}
        <Button color="secondary" onClick={onClose}>
          Fechar
        </Button>
      </ModalFooter>
    </Modal>
  );
}
